import React, { useEffect, useState } from "react";

// Chrome and Edge offer to translate the page, and many of our clients take it (Spanish
// speakers, most of them). Chrome replaces every text node with <font> elements that hold the
// translation. React still points at the old text node, now out of the page: a value it
// updates there never shows ("We counted pages in your files", with no number), and removing
// that node, or inserting something before it, throws and blanks the page.
//
// Two ways to stay right while translated:

// A value the translator must leave alone (amounts, page counts, dates, emails, order numbers,
// file names): it is not translated, so React keeps updating it in place, and "$24.95" can't
// come back as "24,95 $".
export const NoTranslate = ({ as: Tag = "span", className, children, ...rest }) => (
  <Tag translate="no" className={className ? `notranslate ${className}` : "notranslate"} {...rest}>
    {children}
  </Tag>
);

// Dollar amounts inside a text ("Pay $74.85", "−$7.49", and "US$74.85" in the Spanish checkout).
const AMOUNT = /([−-]?(?:US)?\$\d[\d,]*(?:\.\d+)?)/;

// Text that changes and should still be translated: every new text gets a new element (keyed
// by the text, or by `on` when the children aren't plain text), which the translator
// translates again, instead of an update the translator never sees. The keyed element is the
// only child of the outer one, so putting it back can't trip on translated neighbours. In
// plain text, dollar amounts are kept out of the translation.
export const FreshText = ({ as: Tag = "span", on, className, children, ...rest }) => {
  const parts = React.Children.toArray(children);
  const plain = parts.every((part) => typeof part === "string" || typeof part === "number");
  const text = plain ? parts.join("") : null;
  const content = plain
    ? text.split(AMOUNT).map((piece, i) => (i % 2 ? <NoTranslate key={i}>{piece}</NoTranslate> : piece))
    : children;
  return (
    <Tag className={className} {...rest}>
      <span key={on !== undefined ? String(on) : text}>{content}</span>
    </Tag>
  );
};

// Last resort: when a translated page still trips React ("Failed to execute 'removeChild' on
// 'Node'"), draw the page again from scratch instead of leaving it blank. The checkout keeps
// what was entered on earlier steps in the saved order, so the step comes back with it; what
// was typed on the step itself comes back through useKeptAfterRedraw. Any other error goes up
// as before.
let redrawnAt = 0;
const onScreen = {};

// A step's fields as they were on screen, returned only to the step drawn again right after a
// redraw (null any other time, so nothing changes for everyone else).
export const useKeptAfterRedraw = (step, values) => {
  const [kept] = useState(() => (Date.now() - redrawnAt < 5000 ? onScreen[step] || null : null));
  useEffect(() => {
    onScreen[step] = values;
  });
  return kept;
};

export const isTranslationDomError = (error) =>
  !!error && (error.name === "NotFoundError" || /Failed to execute '(removeChild|insertBefore)' on 'Node'/.test(error.message || ""));

export class TranslationBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, attempt: 0 };
    this.redraws = [];
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  // At most 3 redraws in 10 seconds, so an error that keeps coming back isn't hidden.
  canRedraw() {
    const now = Date.now();
    this.redraws = this.redraws.filter((t) => now - t < 10000);
    return isTranslationDomError(this.state.error) && this.redraws.length < 3;
  }

  componentDidCatch(error) {
    if (this.canRedraw()) {
      this.redraws.push(Date.now());
      redrawnAt = Date.now();
      console.warn("The page was redrawn after the browser's translation changed it:", error.message);
      this.setState((s) => ({ error: null, attempt: s.attempt + 1 }));
    }
  }

  render() {
    if (this.state.error) {
      if (!this.canRedraw()) {
        throw this.state.error;
      }
      return null;
    }
    return <React.Fragment key={this.state.attempt}>{this.props.children}</React.Fragment>;
  }
}
